import { Box, Collapse, Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { NotificationCounter } from '@televet/kibble-ui/build/components/NotificationCounter';
import debounce from 'lodash-es/debounce';
import isEqual from 'lodash-es/isEqual';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import { useConversationsContext } from 'pages/Conversations/context/ConversationsContext';
import { ChannelSearchInputKeys, ChannelSearchInputUpdates } from 'pages/Conversations/interfaces/ChannelSearchInput';
import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConversationsList } from 'shared/components/ConversationsList';
import { useAuth } from 'shared/providers/AuthProvider';
import { useInfiniteScrollFetch } from '../../../../../shared/hooks/useInfiniteScrollFetch';
import { ChannelSearchList } from '../../ChannelList/ChannelListToolbar/components/ChannelSearchList';
import { defaultTeletriageFilters } from '../hooks/useTeletriageConversationsList';
import { TeletriageConversationCard } from './TeletriageConversationCard';

interface TeletriageConversationCenterDrawerProps {
  containerWidth: string;
  unreadMessagesCount: number;
  unreadChannelIds: string[];
}

export const TeletriageConversationCenterDrawer = ({
  containerWidth,
  unreadMessagesCount,
  unreadChannelIds,
}: TeletriageConversationCenterDrawerProps): JSX.Element => {
  const { isTeletriageConversationCenterOpen, onTeletriageConversationCenterClose, teletriageConversationCenter } =
    useConversationsContext();

  const {
    // Conversations
    conversations,
    sortByLastMessageEnabled,
    hasLoadedInitialList,
    totalConversationsCount,
    isLoading: isLoadingConversations,
    loadMoreConversations,

    filters,
    isSearchingOpen,
    isSearching,
    setSearchInput,
    toggleSearch,
    totalConversationsCount: conversationsCount,
    clearFilters,
    toggleShowArchived,
  } = teletriageConversationCenter;
  const conversationListContainerRef = useRef<HTMLDivElement | null>(null);
  const { isImpersonating } = useAuth();

  const hasNextPage = useMemo(
    () => (hasLoadedInitialList ? totalConversationsCount > conversations.length : true),
    [conversations.length, hasLoadedInitialList, totalConversationsCount],
  );

  const getNextPage = useCallback(() => {
    if (hasNextPage) {
      loadMoreConversations();
    }
  }, [hasNextPage, loadMoreConversations]);

  const { isLoadingNextPage, listEndRef: conversationListEndRef } = useInfiniteScrollFetch({
    containerRef: conversationListContainerRef,
    hasInitialLoadCompleted: hasLoadedInitialList,
    isLoading: isLoadingConversations,
    fetchNextPage: getNextPage,
  });

  const showEmptyState = useMemo(() => {
    if (isLoadingConversations) {
      return false;
    }

    return !isLoadingConversations && totalConversationsCount === 0;
  }, [isLoadingConversations, totalConversationsCount]);

  const filterToolbarHeight = 56.99;
  const headerHeight = 56;
  const impersonatingMessageHeight = 41;

  const excludedElementsHeight = useMemo(
    () => headerHeight + filterToolbarHeight + (isImpersonating ? impersonatingMessageHeight : 0),
    [isImpersonating],
  );

  const [searchTextPetParentNames, setSearchTextPetParentNames] = useState('');
  const [searchTextPetNames, setSearchTextPetNames] = useState('');
  const [searchTextMessageBodies, setSearchTextMessageBodies] = useState('');
  const [isTouched, setIsTouched] = useState(false);
  const searchTextClientNameInputRef = useRef<HTMLInputElement | null>(null);
  const searchTextClientNameInputRefCallback = useCallback((element: HTMLInputElement | null) => {
    searchTextClientNameInputRef.current = element;
    element?.focus();
  }, []);

  // Clear search on close
  useEffect(() => {
    if (!isSearchingOpen) {
      setSearchTextPetParentNames('');
      setSearchTextPetNames('');
      setSearchTextMessageBodies('');
      setSearchInput({});
      setIsTouched(false);
    }
  }, [isSearchingOpen, setSearchInput]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchConversations = useCallback<
    ((updates: ChannelSearchInputUpdates) => void) & { flush: () => void | undefined }
  >(
    debounce(
      (updates: ChannelSearchInputUpdates): void => {
        setSearchInput(mapValues(updates, (value) => value?.trim()));
      },
      340,
      { leading: false, trailing: true },
    ),
    [],
  );

  const handleSearchInput = useCallback(
    (updates: ChannelSearchInputUpdates): void => {
      if (isSearchingOpen && isTouched) {
        searchConversations(updates);
      }
    },
    [isSearchingOpen, isTouched, searchConversations],
  );

  useEffect(() => {
    handleSearchInput({
      [ChannelSearchInputKeys.PetParentNames]: searchTextPetParentNames,
      [ChannelSearchInputKeys.PetNames]: searchTextPetNames,
      [ChannelSearchInputKeys.MessageBodies]: searchTextMessageBodies,
    });
  }, [searchTextPetParentNames, searchTextPetNames, searchTextMessageBodies, handleSearchInput]);

  const handleChannelSearchInputChange = useCallback(
    (key: ChannelSearchInputKeys, event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (key === ChannelSearchInputKeys.PetParentNames) setSearchTextPetParentNames(value);
      if (key === ChannelSearchInputKeys.PetNames) setSearchTextPetNames(value);
      if (key === ChannelSearchInputKeys.MessageBodies) setSearchTextMessageBodies(value);
    },
    [],
  );

  const handleSearchInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isTouched) setIsTouched(true);
      if (event.key === 'Escape' || event.key === 'Esc') {
        toggleSearch(false);
      } else if (event.key === 'Enter' && !isLoadingConversations) {
        searchConversations({
          [ChannelSearchInputKeys.PetParentNames]: searchTextPetParentNames,
          [ChannelSearchInputKeys.PetNames]: searchTextPetNames,
          [ChannelSearchInputKeys.MessageBodies]: searchTextMessageBodies,
        });
        searchConversations.flush();
      }
    },
    [
      isTouched,
      isLoadingConversations,
      toggleSearch,
      searchConversations,
      searchTextPetParentNames,
      searchTextPetNames,
      searchTextMessageBodies,
    ],
  );

  const isNonArchivedFilterApplied = useMemo(() => {
    return !isEqual(omit(filters, 'showArchived'), omit(defaultTeletriageFilters, 'showArchived'));
  }, [filters]);

  return (
    <Flex
      className="TeletriageConversationCenter__Drawer"
      flex="0 0 auto"
      direction="column"
      w={containerWidth}
      height={
        isTeletriageConversationCenterOpen
          ? { base: `calc(100vh - ${64}px)`, md: `calc(100vh - ${excludedElementsHeight}px)` }
          : 0
      }
      background="background.subtle"
      borderRightWidth="thin"
      borderRightColor="border.default"
      transition="height 0.5s ease-in-out"
      overflowY="hidden"
    >
      {isTeletriageConversationCenterOpen && (
        <>
          <Flex
            className="TeletriageConversationCenter__Drawer--Header"
            alignItems="center"
            justifyContent="space-between"
            px={1}
            py={2}
            mx={2}
          >
            <Flex alignItems="center">
              <Heading size="md" ml={2}>
                Teletriage
              </Heading>
              {unreadMessagesCount > 0 && <NotificationCounter count={unreadMessagesCount} />}
            </Flex>
            <Button variant="ghostNeutral" iconName="chevronDown" onClick={onTeletriageConversationCenterClose} />
          </Flex>
          <Box mx={2}>
            <Collapse in={isSearchingOpen}>
              <Box
                className="TeletriageConversationCenter__Toolbar--Search"
                borderWidth="1px"
                borderBottomColor="border.default"
                backgroundColor="white"
                borderRadius="md"
                mb={2}
              >
                <ChannelSearchList
                  searchTextMessageBodies={searchTextMessageBodies}
                  searchTextPetNames={searchTextPetNames}
                  searchTextPetParentNames={searchTextPetParentNames}
                  searchTextClientNameInputRefCallback={searchTextClientNameInputRefCallback}
                  onSearchInputChange={handleChannelSearchInputChange}
                  onSearchInputKeyDown={handleSearchInputKeyDown}
                  onClose={(): void => toggleSearch(false)}
                  showArchived={filters.showArchived}
                  isFilterApplied={isNonArchivedFilterApplied}
                  onRemoveFilters={clearFilters}
                  onShowArchivedToggle={toggleShowArchived}
                  showSearchResultsCount={isSearching}
                  searchResultsCount={conversationsCount}
                />
              </Box>
            </Collapse>
          </Box>
          <ConversationsList
            containerRef={conversationListContainerRef}
            listEndRef={conversationListEndRef}
            hasNextPage={hasNextPage}
            showEmptyState={showEmptyState}
            containerProps={{
              mx: 2,
              mb: 2,
            }}
          >
            {!!conversations.length &&
              conversations.map((conversation) => {
                return (
                  <TeletriageConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    sortByLastMessageEnabled={sortByLastMessageEnabled}
                    isLoadingNextPage={isLoadingNextPage}
                    hasUnreadMessages={unreadChannelIds.includes(conversation.id)}
                  />
                );
              })}
          </ConversationsList>
        </>
      )}
    </Flex>
  );
};
